import React, { memo, useMemo, useRef, useCallback } from 'react';
import type { PreloadedQuery } from 'react-relay';
import Placeholder from '@atlaskit/react-ufo/placeholder';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { HorizontalNavSkeleton } from '@atlassian/jira-horizontal-nav-common/src/ui/horizontal-nav-skeleton/index.tsx';
import { horizontalNavJiraResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/horizontal-navigation/horizontal-nav-jira/index.tsx';
import { ErrorBoundary as NavigationErrorBoundary } from '@atlassian/jira-horizontal-nav-error-boundary/src/ui/agg-error-boundary/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { showFlag } from '@atlassian/jira-flags/src/services/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { getFlagDetails } from '@atlassian/jira-horizontal-nav-common/src/common/get-flag-details/getFlagDetails.tsx';
import type { HorizontalNavJiraInnerQuery } from '@atlassian/jira-relay/src/__generated__/HorizontalNavJiraInnerQuery.graphql';
import HorizontalNavJiraInner from './HorizontalNavJiraInner.tsx';

/**
 * KEEP CODE OUT OF HERE **shakes fist**
 *
 * This is the code that consumes the deprecated useRelayResource.
 *
 * We want to keep this file as small as possible, only the functionality that entrypoints will eventually replace should
 * be in here such as experience tracking and fallbacks.
 *
 * When we move to entrypoints, this file goes away and the entrypoint provides HorizontalNavJiraInner the queryReference.
 * Doing this becomes harder if code is added here, so don't! Pretend like HorizontalNavJiraInner is the highest level
 * of component and put stuff there.
 */
const HorizontalNavJira = () => {
	const handleError = useCallback((error: Error) => {
		const statusCode = error && 'statusCode' in error ? Number(error.statusCode) : 0;
		const flagDetails = getFlagDetails(statusCode);

		showFlag(flagDetails);
	}, []);

	const analyticsData = useMemo(
		() => ({
			navigationLayer: 'container',
			containerType: 'project',
		}),
		[],
	);

	return (
		<JSErrorBoundary
			id="JiraHorizontalNavigationErrorBoundary"
			packageName="jiraHorizontalNav"
			onError={(_, error) => handleError(error)}
			fallback={HorizontalNavSkeleton}
		>
			<ContextualAnalyticsData attributes={analyticsData}>
				<NavigationErrorBoundary fallback={<HorizontalNavSkeleton />} handleError={handleError}>
					<Placeholder name="JiraHorizontalNavigation" fallback={<HorizontalNavSkeleton />}>
						<HorizontalNavJiraUsingRelayResource />
					</Placeholder>
				</NavigationErrorBoundary>
			</ContextualAnalyticsData>
		</JSErrorBoundary>
	);
};

const entrypoints = {};
const props = {};
const extraProps = {};

const HorizontalNavJiraUsingRelayResource = memo(() => {
	const { queryReference } =
		useRelayResource<HorizontalNavJiraInnerQuery>(horizontalNavJiraResource);
	const queryReferenceRef = useRef<PreloadedQuery<HorizontalNavJiraInnerQuery> | null>(
		queryReference,
	);

	if (queryReference) {
		queryReferenceRef.current = queryReference;
	}

	if (!queryReferenceRef.current || !queryReference) {
		return <HorizontalNavSkeleton />;
	}

	return (
		<HorizontalNavJiraInner
			queries={{
				horizontalNavQuery: queryReferenceRef.current,
			}}
			entryPoints={entrypoints}
			props={props}
			extraProps={extraProps}
		/>
	);
});

export default memo(HorizontalNavJira);
